import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Box, Typography, Dialog, DialogContent, Grow } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { ThemeScope, Button, Input, Label, Card, CardHeader, CardContent, CardFooter, CardDescription, Alert, Badge, Separator } from "./ui/shadcn";

const MotionCard = motion.create(Card);
const MotionButton = motion.create(Button);

function LoginDesign01({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();
  const reduce = useReducedMotion();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ── Motion helpers (visual only) ──
  const ease = [0.22, 1, 0.36, 1];
  const itemV = {
    hidden: { opacity: 1, y: reduce ? 0 : 16 },
    show: { opacity: 1, y: 0, transition: { duration: reduce ? 0 : 0.55, ease } },
  };
  // Form items never use opacity 0, so inputs can never get stuck invisible.
  const fieldV = {
    hidden: { y: reduce ? 0 : 12 },
    show: { y: 0, transition: { duration: reduce ? 0 : 0.5, ease } },
  };
  const stagger = (gap = 0.07, delay = 0.05) => ({
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : gap, delayChildren: reduce ? 0 : delay } },
  });
  const hoverTap = reduce ? {} : { whileHover: { y: -2 }, whileTap: { scale: 0.985 } };

  const features = [
    { icon: AccountBalanceOutlinedIcon, title: "Smart Accounting", text: "Multi-company ledgers, vouchers and real-time reports." },
    { icon: ReceiptLongOutlinedIcon, title: "GST Billing", text: "Compliant invoices, e-invoice, e-way bill and filings." },
    { icon: Inventory2OutlinedIcon, title: "Inventory Control", text: "Track stock, products and purchases across locations." },
  ];

  const capsHelper = capsLockOn ? (
    <div className="ld1-caps" role="status">
      <WarningAmberIcon sx={{ fontSize: 14 }} />Caps Lock is ON
    </div>
  ) : (
    <div className="ld1-caps-space" aria-hidden="true" />
  );

  const patternSvg =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='72' height='72' viewBox='0 0 72 72'%3E%3Cg fill='none' stroke='%23ffffff' stroke-opacity='0.07' stroke-width='1'%3E%3Cpath d='M36 4 68 36 36 68 4 36Z'/%3E%3Cpath d='M36 20 52 36 36 52 20 36Z'/%3E%3C/g%3E%3C/svg%3E\")";

  const themeVars = {
    "--background": "#ffffff",
    "--foreground": "#0f172a",
    "--card": "#ffffff",
    "--card-foreground": "#0f172a",
    "--primary": "#1d4ed8",
    "--primary-foreground": "#ffffff",
    "--secondary": "#eaf1ff",
    "--secondary-foreground": "#1e3a8a",
    "--muted": "#f1f5f9",
    "--muted-foreground": "#64748b",
    "--accent": "#eff4ff",
    "--destructive": "#dc2626",
    "--destructive-foreground": "#ffffff",
    "--border": "#e5ebf4",
    "--input": "#b4c2d8",
    "--ring": "#2563eb",
    "--radius": "0.75rem",
  };

  const errorBlock = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key="err"
          initial={{ opacity: 1, height: 0 }}
          animate={{ opacity: 1, height: "auto", x: reduce ? 0 : [0, -6, 6, -4, 4, 0] }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: reduce ? 0 : 0.45 }}
          style={{ overflow: "hidden" }}
        >
          <Alert variant="destructive" className="ld1-alert">
            <WarningAmberIcon sx={{ fontSize: 17, flexShrink: 0 }} />
            <span>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <ThemeScope vars={themeVars} className="ld1-font ld1-root">
      <style>{[
        "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
        ".ld1-font, .ld1-font * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
        "@keyframes ld1FadeUp { from { transform: translateY(16px); } to { opacity: 1; transform: none; } }",
        "@keyframes ld1Pattern { from { background-position: 0 0; } to { background-position: 72px 72px; } }",
        ".ld1-root { min-height: 100vh; min-height: 100dvh; width: 100%; max-width: 100vw; overflow-x: hidden; display: flex; flex-direction: column; background: var(--background); }",
        ".ld1-left { position: relative; overflow: hidden; flex-shrink: 0; width: 100%; display: flex; flex-direction: column; justify-content: center; gap: 0; padding: 14px 16px; color: #fff; text-align: center; background: linear-gradient(155deg, #06143a 0%, #0b2a6f 50%, #1d4ed8 120%); }",
        ".ld1-left-inner { position: relative; z-index: 1; display: flex; flex-direction: column; gap: 0; width: 100%; min-width: 0; }",
        ".ld1-logo { height: 36px; max-width: 100%; filter: brightness(0) invert(1); align-self: center; }",
        ".ld1-eyebrow { margin: 0; color: #93c5fd; font-size: .72rem; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; }",
        ".ld1-h2 { margin: 0; font-size: clamp(1.8rem, 1.1rem + 2.2vw, 2.9rem); font-weight: 800; line-height: 1.15; letter-spacing: -.5px; }",
        ".ld1-h2 span { color: #93c5fd; }",
        ".ld1-desc { margin: 0; color: rgba(255,255,255,.65); font-size: .95rem; line-height: 1.75; max-width: 440px; }",
        ".ld1-features { list-style: none; margin: 0; padding: 0; flex-direction: column; gap: 18px; }",
        ".ld1-feature { display: flex; align-items: flex-start; gap: 16px; text-align: left; }",
        ".ld1-feature-icon { width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.16); backdrop-filter: blur(4px); }",
        ".ld1-feature h3 { margin: 0; font-size: .98rem; font-weight: 700; line-height: 1.3; }",
        ".ld1-feature p { margin: 3px 0 0; color: rgba(255,255,255,.6); font-size: .85rem; line-height: 1.55; }",
        ".ld1-foot-note { margin: 0; color: rgba(255,255,255,.4); font-size: .75rem; }",
        ".ld1-pattern { position: absolute; inset: 0; pointer-events: none; background-size: 72px 72px; animation: ld1Pattern 40s linear infinite; -webkit-mask-image: linear-gradient(180deg, rgba(0,0,0,.9), rgba(0,0,0,.35)); mask-image: linear-gradient(180deg, rgba(0,0,0,.9), rgba(0,0,0,.35)); }",
        ".ld1-glow { position: absolute; top: -120px; left: -100px; width: 360px; height: 360px; border-radius: 50%; background: radial-gradient(circle, rgba(59,130,246,.3) 0%, transparent 70%); pointer-events: none; }",
        ".ld1-diamond { position: absolute; border: 1px solid rgba(147,197,253,.22); pointer-events: none; display: none; }",
        ".ld1-md { display: none; }",
        ".ld1-right { position: relative; z-index: 1; flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: center; padding: clamp(12px, 4vw, 40px) clamp(12px, 4vw, 40px) 20px; background: #f1f5f9; }",
        ".ld1-card-wrap { width: 100%; min-width: 0; max-width: 440px; margin: auto 0; }",
        ".ld1-card { --ld1-pad: clamp(18px, 5.5vw, 36px); min-width: 0; border-radius: 20px; box-shadow: 0 12px 40px rgba(15,23,42,.08); overflow: hidden; }",
        ".ld1-card .ui-card-header { padding: var(--ld1-pad) var(--ld1-pad) 18px; }",
        ".ld1-card .ui-card-content { padding: 0 var(--ld1-pad) var(--ld1-pad); }",
        ".ld1-card .ui-card-footer { padding: 14px var(--ld1-pad) calc(var(--ld1-pad) - 6px); justify-content: center; }",
        ".ld1-card .ui-card-title { font-size: clamp(1.45rem, 1.2rem + 1vw, 1.85rem); line-height: 1.15; font-weight: 800; overflow-wrap: anywhere; letter-spacing: -.5px; }",
        ".ld1-card .ui-card-description { font-size: .9rem; }",
        ".ld1-field { margin-bottom: 14px; }",
        ".ld1-field .ui-label { margin-bottom: 8px; color: var(--foreground); }",
        ".ld1-field .ui-input { min-height: 48px; height: 48px; font-size: .92rem; border-radius: 10px; background: #f8fafc; color: #0f172a; -webkit-text-fill-color: #0f172a; position: relative; z-index: 1; }",
        ".ld1-field .ui-input::placeholder { color: #64748b; -webkit-text-fill-color: #64748b; opacity: 1; }",
        ".ld1-field .ui-input:focus-visible { background: #fff; border-color: var(--ring); }",
        ".ld1-field .ui-input:hover { border-color: var(--ring); }",
        ".ld1-pw { position: relative; }",
        ".ld1-pw .ui-input { padding-right: 52px; }",
        ".ld1-eye { z-index: 2; position: absolute; right: 2px; top: 50%; transform: translateY(-50%); width: 44px; height: 44px; color: var(--muted-foreground); }",
        ".ld1-eye:active { transform: translateY(-50%); }",
        ".ld1-caps { display: flex; align-items: center; gap: 4px; margin-top: 6px; color: #b45309; font-size: 12px; min-height: 18px; }",
        ".ld1-caps-space { min-height: 0; }",
        ".ld1-row-end { display: flex; justify-content: flex-end; margin: -10px 0 8px; }",
        ".ld1-link { display: inline-flex; align-items: center; min-height: 44px; padding: 0 4px; color: var(--primary); font-weight: 600; text-decoration: none; font-size: .82rem; border-radius: 4px; }",
        ".ld1-link:hover { text-decoration: underline; text-underline-offset: 3px; }",
        ".ld1-link:focus-visible { outline: 2px solid var(--ring); outline-offset: 2px; }",
        ".ld1-alert span { min-width: 0; overflow-wrap: anywhere; }",
        ".ld1-alert { display: flex; align-items: center; gap: 8px; background: #fef2f2; margin-bottom: 16px; }",
        ".ld1-primary { width: 100%; height: 48px; font-size: .95rem; font-weight: 700; border-radius: 10px; background: linear-gradient(135deg, #1d4ed8 0%, #0b2a6f 100%); box-shadow: 0 6px 18px rgba(29,78,216,.28); }",
        ".ld1-primary:hover { opacity: 1; box-shadow: 0 10px 24px rgba(29,78,216,.38); filter: brightness(1.08); }",
        ".ld1-secondary-btn { width: 100%; height: 48px; margin-top: 12px; border-radius: 10px; color: var(--primary); }",
        ".ld1-signup { color: var(--muted-foreground); font-size: .875rem; margin: 0; text-align: center; }",
        ".ld1-signup a { display: inline-block; padding: 12px 4px; margin: -12px 0; color: var(--primary); font-weight: 700; text-decoration: none; }",
        ".ld1-signup a:hover { text-decoration: underline; text-underline-offset: 3px; }",
        ".ld1-copy { margin: 20px 0 0; text-align: center; color: #94a3b8; font-size: .73rem; flex-shrink: 0; }",
        ".ld1-companies { display: flex; flex-direction: column; gap: 10px; margin-bottom: 18px; max-height: 46dvh; overflow-y: auto; padding: 4px; margin-left: -4px; margin-right: -4px; }",
        ".ld1-company { min-width: 0; min-height: 56px; display: flex; align-items: center; gap: 12px; padding: 14px; cursor: pointer; border-radius: 12px; transition: border-color .15s, background-color .15s, box-shadow .15s; outline: none; box-shadow: none; }",
        ".ld1-company:hover { border-color: var(--ring); }",
        ".ld1-company:focus-visible { box-shadow: 0 0 0 2px var(--background), 0 0 0 4px var(--ring); }",
        ".ld1-company[aria-checked='true'] { border-color: var(--primary); background: var(--accent); box-shadow: 0 0 0 1px var(--primary); }",
        ".ld1-company-ico { width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: var(--secondary); color: var(--primary); }",
        ".ld1-company-name { flex: 1; min-width: 0; font-weight: 600; font-size: .92rem; overflow-wrap: anywhere; }",
        "@media (min-width: 600px) { .ld1-left { padding: 18px 24px; } .ld1-logo { height: 40px; } }",
        "@media (min-width: 900px) {",
        "  .ld1-root { flex-direction: row; }",
        "  .ld1-left { width: 50%; min-height: 100dvh; justify-content: space-between; align-items: flex-start; text-align: left; padding: 44px 40px; gap: 0; }",
        "  .ld1-left-inner { gap: 0; flex: 1; justify-content: space-between; }",
        "  .ld1-logo { height: 48px; align-self: flex-start; }",
        "  .ld1-h2 { margin-bottom: 14px; }",
        "  .ld1-eyebrow { margin-bottom: 12px; }",
        "  .ld1-desc { margin-bottom: 32px; }",
        "  .ld1-md { display: block; }",
        "  .ld1-features.ld1-md { display: flex; }",
        "  .ld1-diamond { display: block; }",
        "  .ld1-right { background: #fff; padding: 40px; min-height: 100dvh; }",
        "  .ld1-card { box-shadow: 0 20px 60px rgba(15,23,42,.07); }",
        "}",
        "@media (min-width: 1200px) { .ld1-left { padding: 56px clamp(64px, 6vw, 112px); } }",
        "@media (min-width: 1600px) { .ld1-card-wrap { max-width: 480px; } .ld1-desc { max-width: 500px; } }",
        "@media (max-height: 520px) { .ld1-card .ui-card-header { padding-bottom: 10px; } .ld1-field { margin-bottom: 8px; } .ld1-right { padding-top: 10px; } .ld1-left { padding-top: 8px; padding-bottom: 8px; } .ld1-logo { height: 30px; } }",
        "@media (prefers-reduced-motion: reduce) { .ld1-pattern { animation: none !important; } }",
      ].join("\n")}</style>

      {/* ── LEFT BRAND PANEL ── */}
      <aside className="ld1-left">
        <div aria-hidden="true" className="ld1-pattern" style={{ backgroundImage: patternSvg }} />
        <div aria-hidden="true" className="ld1-glow" />
        <motion.div
          aria-hidden="true"
          className="ld1-diamond"
          style={{ right: -130, bottom: -130, width: 380, height: 380, borderRadius: 28, rotate: 45 }}
          animate={reduce ? undefined : { scale: [1, 1.05, 1], opacity: [0.9, 1, 0.9] }}
          transition={{ duration: 0.01, repeat: 0, ease: "easeInOut" }}
        />
        <div aria-hidden="true" className="ld1-diamond" style={{ right: -80, bottom: -80, width: 260, height: 260, borderRadius: 20, transform: "rotate(45deg)", borderColor: "rgba(147,197,253,.16)" }} />

        <motion.div className="ld1-left-inner" variants={stagger(0.09, 0.05)} initial="hidden" animate="show">
          <motion.div variants={itemV} style={{ display: "flex", justifyContent: "inherit" }}>
            <img className="ld1-logo" src="/RBLOGOBLUE.svg" alt="ReonBook" />
          </motion.div>

          <div className="ld1-md">
            <motion.p variants={itemV} className="ld1-eyebrow">Enterprise Accounting Platform</motion.p>
            <motion.h2 variants={itemV} className="ld1-h2" style={{ marginTop: 12 }}>
              Run your books with <span>clarity</span>
            </motion.h2>
            <motion.p variants={itemV} className="ld1-desc ld1-md" style={{ marginTop: 14 }}>
              One secure workspace for accounting, GST compliance and inventory — built for growing Indian businesses.
            </motion.p>
            <motion.ul variants={stagger(0.1, 0)} className="ld1-features ld1-md">
              {features.map((f) => {
                const Icon = f.icon;
                return (
                  <motion.li key={f.title} variants={itemV} className="ld1-feature" whileHover={reduce ? undefined : { x: 4 }}>
                    <span aria-hidden="true" className="ld1-feature-icon"><Icon sx={{ fontSize: 22, color: "#bfdbfe" }} /></span>
                    <div>
                      <h3>{f.title}</h3>
                      <p>{f.text}</p>
                    </div>
                  </motion.li>
                );
              })}
            </motion.ul>
          </div>

          <motion.p variants={itemV} className="ld1-foot-note ld1-md">Trusted, secure and always available.</motion.p>
        </motion.div>
      </aside>

      {/* ── RIGHT FORM PANEL ── */}
      <main className="ld1-right">
        <div className="ld1-card-wrap">
          <MotionCard
            className="ld1-card"
            initial={{ opacity: 1, y: reduce ? 0 : 20, scale: reduce ? 1 : 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: reduce ? 0 : 0.6, ease }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {!isCompanySelectionVisible ? (

                /* ── LOGIN FORM ── */
                <motion.div
                  key="login"
                  variants={stagger(0.07, 0.05)}
                  initial="hidden"
                  animate="show"
                  exit={{ opacity: 0, y: reduce ? 0 : -10, transition: { duration: reduce ? 0 : 0.2 } }}
                >
                  <motion.div variants={itemV}>
                    <CardHeader>
                      <h1 className="ui-card-title">Welcome back</h1>
                      <CardDescription>Sign in to access your ReonBook dashboard</CardDescription>
                    </CardHeader>
                  </motion.div>

                  <CardContent>
                    <form noValidate onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>
                      <motion.div variants={fieldV} className="ld1-field">
                        <Label htmlFor="ld1-identifier">Username, Email or Mobile</Label>
                        <Input
                          id="ld1-identifier"
                          type="text"
                          autoComplete="username"
                          placeholder="you@company.com"
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                          onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                        />
                        {capsHelper}
                      </motion.div>

                      <motion.div variants={fieldV} className="ld1-field">
                        <Label htmlFor="ld1-password">Password</Label>
                        <div className="ld1-pw">
                          <Input
                            id="ld1-password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder="Enter your password"
                            value={userPassword}
                            onChange={(e) => setUserPassword(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          />
                          <Button type="button" variant="ghost" size="icon" className="ld1-eye" onClick={handleTogglePasswordVisibility} aria-label={showPassword ? "Hide password" : "Show password"}>
                            {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                          </Button>
                        </div>
                        {capsHelper}
                      </motion.div>

                      <motion.div variants={fieldV} className="ld1-row-end">
                        <Link to="/forgot-password" className="ld1-link">Forgot password?</Link>
                      </motion.div>

                      {errorBlock}

                      <motion.div variants={fieldV}>
                        <MotionButton type="submit" size="lg" className="ld1-primary" disabled={isLoading} {...hoverTap}>
                          {isLoading
                            ? <><CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />Signing in...</>
                            : "Sign In"}
                        </MotionButton>

                        {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                          <Button type="button" variant="outline" className="ld1-secondary-btn"
                            onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                            Login with Biometric
                          </Button>
                        )}
                      </motion.div>
                    </form>
                  </CardContent>

                  <Separator />
                  <motion.div variants={itemV}>
                    <CardFooter>
                      <p className="ld1-signup">
                        Don't have an account?{" "}
                        <Link to="/register">Sign up now</Link>
                      </p>
                    </CardFooter>
                  </motion.div>
                </motion.div>

              ) : (

                /* ── COMPANY SELECTION ── */
                <motion.div
                  key="company"
                  variants={stagger(0.07, 0.05)}
                  initial="hidden"
                  animate="show"
                  exit={{ opacity: 0, y: reduce ? 0 : -10, transition: { duration: reduce ? 0 : 0.2 } }}
                >
                  <motion.div variants={itemV}>
                    <CardHeader>
                      <h1 className="ui-card-title">Select Organization</h1>
                      <CardDescription>Choose which organization to access</CardDescription>
                    </CardHeader>
                  </motion.div>

                  <CardContent>
                    <motion.div variants={stagger(0.06, 0)} className="ld1-companies" role="radiogroup" aria-label="Choose your organization">
                      {companies.map((c) => {
                        const active = selectedCompany?.companyId === c.companyId;
                        return (
                          <MotionCard
                            key={c.companyId}
                            variants={itemV}
                            role="radio"
                            aria-checked={active}
                            tabIndex={0}
                            className="ld1-company"
                            onClick={() => setSelectedCompany(c)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedCompany(c); }
                            }}
                            {...hoverTap}
                          >
                            <span aria-hidden="true" className="ld1-company-ico"><BusinessOutlinedIcon sx={{ fontSize: 20 }} /></span>
                            <span className="ld1-company-name">{c.companyName}</span>
                            {active && <Badge variant="secondary"><CheckIcon sx={{ fontSize: 13, mr: 0.4 }} />Selected</Badge>}
                          </MotionCard>
                        );
                      })}
                    </motion.div>

                    {errorBlock}

                    <motion.div variants={itemV}>
                      <MotionButton type="button" size="lg" className="ld1-primary" onClick={() => FN_SelectCompany()} {...hoverTap}>
                        Continue to Dashboard →
                      </MotionButton>
                    </motion.div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </MotionCard>
        </div>

        <p className="ld1-copy">
          <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
          {new Date().getFullYear()} Powered by{" "}
          Soltrack Technologies Pvt Ltd
        </p>
      </main>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="ld1-font"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={reduce ? 0 : { enter: 320, exit: 180 }}
        BackdropProps={{ sx: { backdropFilter: "blur(3px)", bgcolor: "rgba(15,23,42,0.45)" } }}
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", m: 2, width: "calc(100% - 32px)" } }}
      >
        <DialogContent sx={{ p: 0 }}>
<ThemeScope vars={themeVars}>
          {/* Top banner */}
          <Box sx={{ background: "linear-gradient(135deg, #06143a 0%, #1d4ed8 100%)", px: 3, pt: 3, pb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <DevicesIcon sx={{ color: "#fff", fontSize: 22 }} />
              </Box>
              <Box>
                <Typography fontWeight={800} fontSize="1.05rem" color="#fff" lineHeight={1.3}>
                  Session Limit Reached
                </Typography>
                <Typography fontSize="0.78rem" color="rgba(255,255,255,0.72)" mt={0.2}>
                  {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                </Typography>
              </Box>
            </Box>
            <Box sx={{ bgcolor: "rgba(255,255,255,0.12)", borderRadius: 1.5, px: 2, py: 1 }}>
              <Typography fontSize="0.8rem" color="rgba(255,255,255,0.85)" lineHeight={1.5}>
                Sign out of one of the devices below to log in on this device.
              </Typography>
            </Box>
          </Box>

          {/* Device list */}
          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Typography fontSize="0.72rem" fontWeight={700} color="#94a3b8" letterSpacing="0.8px" textTransform="uppercase" mb={1.5}>
              Active Sessions
            </Typography>

            {sessionPickerData?.activeSessions?.map((session, idx) => (
              <Box key={session.id}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  p: 1.5, mb: 1, borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  bgcolor: "#f8fafc",
                  animation: reduce ? "none" : "ld1FadeUp .45s cubic-bezier(.22,1,.36,1) " + (0.1 + idx * 0.07) + "s backwards",
                  transition: "border-color .2s ease, transform .2s ease, box-shadow .2s ease",
                  "&:hover": { borderColor: "#2563eb", transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(37,99,235,0.12)" },
                }}>
                {/* Icon */}
                <Box sx={{ width: 38, height: 38, borderRadius: 1.5, bgcolor: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {deviceIcon(session.device_type)}
                </Box>

                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} fontSize="0.87rem" color="#0f172a" noWrap>
                    {session.device_name || "Unknown Device"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                    <AccessTimeIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                    <Typography fontSize="0.73rem" color="#94a3b8">
                      {timeAgo(session.last_activity)}
                    </Typography>
                    {session.ip_address && (
                      <Typography fontSize="0.73rem" color="#94a3b8" ml={0.5} noWrap>
                        · {session.ip_address}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Action */}
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={kickingSessionId === session.id}
                  onClick={async () => {
                    setKickingSessionId(session.id);
                    setSessionPickerData(null);
                    await FN_HandleLogin(identifier, userPassword, session.id);
                    setKickingSessionId(null);
                  }}
                  style={{ minWidth: 84, minHeight: 44, flexShrink: 0, fontWeight: 700, background: "#dc2626", color: "#fff" }}
                >
                  {kickingSessionId === session.id
                    ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                    : "Sign Out"}
                </Button>
              </Box>
            ))}
          </Box>

          {/* Cancel */}
          <Box sx={{ px: 2.5, pb: 2.5, pt: 0.5, textAlign: "center" }}>
            <Button type="button" variant="outline" onClick={() => setSessionPickerData(null)} style={{ width: "100%", minHeight: 44, color: "#64748b", fontWeight: 600 }}>
              Cancel
            </Button>
          </Box>
</ThemeScope>
        </DialogContent>
      </Dialog>

    </ThemeScope>
  );
}

export default LoginDesign01;
